<template>
  <div class="magazine-overview" data-track-as="magazine">
    <div
      class="magazine-overview-header"
      :class="{ 'magazine-overview-header--has-image': headerImage }"
    >
      <header-image
        v-if="
          (headerImage || logo) && !(!headerImage && socialRegistrationOpen)
        "
        class="magazine-overview-header-header-image"
        :image="headerImageObject"
        :logo="logo"
        :cta-intro-text="ctaIntroText"
        :cta-text="ctaText"
        :cta-link="ctaLink"
        :cta-target="ctaTarget"
        :cta-theme="ctaTheme"
      />
    </div>
    <div
      class="content-area"
      :class="{ 'content-area--no-image': !headerImage }"
    >
      <magazine-meta
        :no-image="!headerImage"
        :registration-form-formid="registrationFormFormid"
        :registration-form-formstart="registrationFormFormstart"
        :registration-form-submit-url="registrationFormSubmitUrl"
        :social-links="socialLinks"
        :navigation-list="navigationList"
        @overlayOpened="val => (socialRegistrationOpen = val)"
      />
      <div
        class="content-column"
        :class="{
          'content-column--hide': socialRegistrationOpen && !headerImage,
        }"
      >
        <slot>slot?</slot>
      </div>
    </div>
    <slot name="footer"></slot>
  </div>
</template>

<script>
import HeaderImage from '@/components/HeaderImage'

import MagazineMeta from '../common/MagazineMeta'

export default {
  name: 'SanitasMagazineOverview',

  components: {
    HeaderImage,
    MagazineMeta,
  },

  props: {
    headerImage: {
      type: String,
      default: '',
    },
    headerImageRatio: {
      type: Number,
      default: 0.5625,
    },
    headerImageSrcset: {
      type: String,
      default: '',
    },
    ctaIntroText: {
      type: String,
      default: '',
    },
    ctaText: {
      type: String,
      default: '',
    },
    ctaLink: {
      type: String,
      default: '',
    },
    ctaTarget: {
      type: String,
      default: '',
    },
    ctaTheme: {
      type: String,
      default: '',
    },
    registrationFormSubmitUrl: {
      type: String,
      default: '',
    },
    registrationFormFormid: {
      type: String,
      default: '',
    },
    registrationFormFormstart: {
      type: String,
      default: '',
    },
    socialLinks: {
      type: String,
      default: '',
    },
    logo: {
      type: String,
      required: true,
    },
    navigationList: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    socialRegistrationOpen: false,
  }),
  computed: {
    headerImageObject() {
      return {
        src: this.headerImage,
        srcSet: this.headerImageSrcset,
        ratio: this.headerImageRatio,
      }
    },
  },
}
</script>

<style lang="postcss">
.magazine-overview {
  position: relative;
  margin-left: calc(-1 * var(--body-offset-horizontal));
  margin-right: calc(-1 * var(--body-offset-horizontal));

  sup {
    top: -0.2em !important;
  }

  .content-area {
    background-color: white;
    max-width: 1300px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    color: var(--c-primary-neutral-1);
    display: flex;
    flex-direction: column;

    h1 {
      width: 80%;
      font-family: 'Faustina', serif;
      font-weight: 700;
      font-size: 60px;
      line-height: 1;
      margin-top: 60px;
      text-align: left;
      margin-bottom: 40px;
      color: var(--c-primary-neutral-1);

      &:empty {
        display: none;
      }
    }

    h2 {
      width: 80%;
      font-family: 'Faustina', serif;
      color: var(--c-primary-neutral-1);
      font-size: 36px;
      margin-bottom: 100px;
      line-height: 1;

      &:empty {
        display: none;
      }
    }

    h1 + h2 {
      margin-top: -10px;
    }

    .content-column {
      max-width: 1100px;
      margin: -50px auto 0 auto;
      display: inline-block;
      position: relative;

      .magazine-registration__link-list {
        left: 0px;
      }
    }
  }

  .content-area--no-image {
    margin-top: 0;

    .content-column {
      margin: 0 auto;
    }

    .content-column--hide {
      margin-top: -50px !important;
    }

    h2 {
      margin-bottom: 50px;
    }

    h1 + h2 {
      margin-top: -15px;
    }

    .meta-area {
      .magazine-registration__link-list {
        left: 0;
        right: auto !important;
      }
    }
  }
}

.magazine-overview-header {
  display: block;
  position: relative;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  max-width: 1660px;
  border-top: solid 1px var(--c-secondary-neutral-2);
}

.magazine-overview-header--has-image {
  border-top: none;
}

.magazine-overview-header--has-image + .content-area {
  margin-top: -200px;
}

@media (max-width: 1200px) {
  .magazine-overview {
    margin-left: calc(-1 * var(--body-offset-horizontal));
    margin-right: calc(-1 * var(--body-offset-horizontal));

    .content-area h1 {
      margin-top: 60px;
    }

    .content-area--no-image {
      margin-top: 0;
    }
  }

  .overviewTitle {
    top: 120px;
  }

  .magazine-overview-header--has-image + .content-area {
    margin-top: 0;
  }
}

@media (max-width: 1160px) {
  .magazine-overview {
    .content-area .content-column {
      margin: -50px 30px 0 30px;
    }

    .content-area--no-image {
      .content-column {
        margin: 0 auto;
        padding: 0 var(--spacing-6);
      }

      .meta-area .magazine-registration__link-list {
        left: 30px;
        right: auto !important;
      }
    }
  }
}

@media (max-width: 860px) {
  .magazine-overview {
    .content-area {
      display: flex;

      .content-column {
        padding: 0 var(--spacing-2);
        margin: -30px 0 0;
        max-width: 100%;
        width: auto;

        .magazine-registration__link-list {
          left: 10px;
        }
      }

      h2 {
        margin-bottom: 50px;
      }
    }

    .content-area--no-image {
      .content-column {
        margin: 0 auto;
      }

      .meta-area {
        .magazine-registration__link-list {
          left: 10px;
          right: auto !important;
        }
      }
    }
  }
}

/* Base on business request */
@media (max-width: 730px) {
  .magazine-overview .content-area {
    h1,
    h2 {
      width: 100%;
    }
  }
}

@media (max-width: 490px) {
  .magazine-overview {
    margin-left: calc(-1 * var(--body-offset-horizontal));
    margin-right: calc(-1 * var(--body-offset-horizontal));

    .content-area {
      h1 {
        font-size: 24px;
        margin-top: 20px;
        margin-bottom: 20px;
      }

      h2 {
        margin-bottom: 20px;
        font-size: 15px;
      }

      .meta-area {
        .magazine-registration__link-list {
          left: 10px;
          right: 10px;
          top: 10px;
          justify-content: space-between;
          width: 100%;
        }
      }
    }

    .content-column {
      margin: 0 auto !important;
    }

    .content-area--no-image {
      .meta-area {
        .magazine-registration__link-list {
          top: 10px;
          left: 10px;
          right: auto !important;
        }
      }
    }
  }

  .overviewTitle {
    top: 65px;
  }
}
</style>
